import { IAreaOccupancyStats, IOccupancyStats, IRestaurantDateStats, Label } from '@localina/core';
import { HouseWithDots } from '@localina/icons';
import React from 'react';
import { useRestaurant } from '../../../api/queries/restaurants';
import { RestaurantStatsFigures } from './RestaurantStatsFigures';
import { RestaurantStatsKpiRow } from './RestaurantStatsKpiRow';
import { RestaurantStatsTimeAxis } from './RestaurantStatsTimeAxis';
import { getDayRange } from './RestaurantStatsTimeline';
import { RestaurantStatsAllDayTile, RestaurantStatsShiftTile } from './RestaurantStatsTile';

interface IProps {
    stats: IRestaurantDateStats;
    previousWeekStats?: IOccupancyStats;
}

const RestaurantDateStats: React.FC<IProps> = (props) => {
    const restaurantQuery = useRestaurant();

    const showArea = (areaOccupancyStat: IAreaOccupancyStats): boolean => {
        return areaOccupancyStat.shiftOccupancyStats.length != 0;
    };

    const filteredAreaOccupancyStats = props.stats.areaOccupancyStats.filter((it) => showArea(it));

    const shifts = filteredAreaOccupancyStats.flatMap((it) => it.shiftOccupancyStats).map((it) => it.shift);
    const range = shifts.length > 0 ? getDayRange(props.stats.occupancyStats.list, shifts) : undefined;

    const getNumberOfReservations = (area: string) => {
        let numberOfReservations = 0;
        props.stats.areaOccupancyStats
            .find((areaOccupancyStat) => areaOccupancyStat.area === area)
            ?.shiftOccupancyStats.forEach(
                (shiftOccupancyStat) => (numberOfReservations += shiftOccupancyStat.stats.numberOfReservations),
            );
        return numberOfReservations;
    };

    const getNumberOfGuests = (area: string) => {
        let numberOfGuests = 0;
        props.stats.areaOccupancyStats
            .find((areaOccupancyStat) => areaOccupancyStat.area === area)
            ?.shiftOccupancyStats.forEach(
                (shiftOccupancyStat) => (numberOfGuests += shiftOccupancyStat.stats.numberOfGuests),
            );
        return numberOfGuests;
    };

    const getAreaById = (id: string) => {
        const virtualAreas = restaurantQuery.data?.configuration.virtualAreas;
        return (
            virtualAreas?.find((virtualArea) => virtualArea.id === id) ??
            virtualAreas?.flatMap((virtualArea) => virtualArea.areas).find((area) => area.id === id)
        );
    };

    return (
        <div className="restaurant-stats__date">
            <RestaurantStatsKpiRow stats={props.stats.occupancyStats} previousWeekStats={props.previousWeekStats} />
            {range && (
                <RestaurantStatsAllDayTile
                    restaurant={restaurantQuery.data}
                    stats={props.stats}
                    previousWeekStats={props.previousWeekStats}
                    range={range}
                >
                    {filteredAreaOccupancyStats.map((areaOccupancyStat: IAreaOccupancyStats) => (
                        <div className="restaurant-stats__area" key={areaOccupancyStat.area}>
                            <div className="restaurant-stats__tile__title">
                                <HouseWithDots size="big" />
                                <Label type="title" value={getAreaById(areaOccupancyStat.area)?.name} variant="h6" />
                            </div>
                            <div className="restaurant-stats__row">
                                <RestaurantStatsFigures
                                    figures={{
                                        numberOfGuests: getNumberOfGuests(areaOccupancyStat.area),
                                        numberOfReservations: getNumberOfReservations(areaOccupancyStat.area),
                                    }}
                                />
                                <div className="restaurant-stats__graph">
                                    <RestaurantStatsShiftTile
                                        stats={areaOccupancyStat}
                                        date={props.stats.date}
                                        range={range}
                                    />
                                    <RestaurantStatsTimeAxis range={range} />
                                </div>
                            </div>
                        </div>
                    ))}
                </RestaurantStatsAllDayTile>
            )}
        </div>
    );
};
export default RestaurantDateStats;
